import React from "react";
import { Formik } from "formik";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { cn } from "../../../lib/utils";
import BirthDayPicker from "../BirthDayPicker/BirthDayPicker";
import GenderSelect from "../GenderSelect/GenderSelect";
import AccountTypeSelect from "../AccountTypeSelect/AccountTypeSelect";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useToast } from "../../ui/use-toast";
import { ToastAction } from "../../ui/toast";
import { axiosPublic } from "../../../api/axiosInstance";
import { REGISTER_SEND_OTP } from "../../../api/apiConstants";
import { useStepper } from "../../ui/stepper";

const SignUpForm = ({ setSignUpInformation, setOTP }) => {
  const { nextStep } = useStepper();
  const { toast } = useToast();
  //State để show/hide password
  const [showPassword, setShowPassword] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  //Function để toggle show/hide password
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  // Handle form submission
  async function handleSubmit(values, setSubmitting, setLoading) {
    try {
      toast({
        title: "Đang xử lý...",
        description: "Vui chờ trong giây lát!",
        action: <ToastAction altText="undo">Ẩn</ToastAction>,
      });
      setLoading(true); // Start loading
      // Step 4: Make the API call to update the user information
      const response = await axiosPublic.post(REGISTER_SEND_OTP, {
        email: values.email,
        password: values.password,
        username: values.username,
        phoneNumber: values.phoneNumber,
        firstName: values.firstName,
        lastName: values.lastName,
        gender: values.gender,
        avatar: values.avatar,
        facebookUrl: values.facebookUrl,
        youtubeUrl: values.youtubeUrl,
        tiktokUrl: values.tiktokUrl,
        birthday: values.birthday,
        accountType: values.accountType,
      });
      if (response.status === 200) {
        toast({
          title: "Lấy mã OTP thành công!",
          description: "Vui lòng kiểm tra mã OTP đã gửi tới email của bạn!",
          action: <ToastAction altText="undo">Ẩn</ToastAction>,
        });
        console.log("Get OTP successfully: ", response.data);
        setOTP(response.data.data);
        setSignUpInformation(values);
        nextStep(); // Move to the next step
      } else {
        // Handle any other status code appropriately
        toast({
          variant: "destructive",
          title: "Có lỗi xảy ra !",
          description: "Vui lòng thử lại!",
          action: <ToastAction altText="undo">Ẩn</ToastAction>,
        });
        console.log("Failed to update profile");
      }
    } catch (error) {
      // Handle error (e.g., show an error message)
      toast({
        variant: "destructive",
        title: "Có lỗi xảy ra !",
        description: "Lỗi: " + error.response.data.message,
        action: <ToastAction altText="undo">Ẩn</ToastAction>,
      });
      console.error("Error get OTP:", error);
    } finally {
      setLoading(false); // Stop loading regardless of the outcome
      setSubmitting(false); // Set Formik submitting to false
    }
  }

  return (
    <>
      <Formik
        initialValues={{
          email: "",
          password: "",
          username: "",
          avatar: "string",
          phoneNumber: "",
          firstName: "",
          lastName: "",
          gender: "",
          facebookUrl: "string",
          youtubeUrl: "string",
          tiktokUrl: "string",
          birthday: null,
          accountType: "",
        }}
        validate={(values) => {
          const errors = {};
          // Email validation
          if (!values.email) {
            errors.email = "Không được để trống!";
          } else if (
            !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)
          ) {
            errors.email = "Email không hợp lệ!";
          }
          // Password validation
          if (!values.password) {
            errors.password = "Không được để trống!";
          } else if (
            !/(?=(.*[0-9]))(?=.*[\!@#$%^&*()\\[\]{}\-_+=~`|:;"'<>,./?])(?=.*[a-z])(?=(.*[A-Z]))(?=(.*)).{8,}/.test(
              values.password
            )
          ) {
            errors.password =
              "Mật khẩu phải bao gồm cả chữ hoa, chữ thường, số, ký tự đặc biệt và ít nhất 8 kỹ tự. Ví dụ: Abc@1234";
          }
          // Birthday validation
          if (!values.birthday) {
            errors.birthday = "Không được để trống!";
          } else {
            // Chuyển đổi values.birthday sang đối tượng Date nếu cần
            const birthday = new Date(values.birthday);
            const today = new Date();
            // Đảm bảo rằng giờ, phút, giây và mili giây không ảnh hưởng đến so sánh
            today.setHours(0, 0, 0, 0);

            if (birthday > today) {
              errors.birthday =
                "Ngày tháng năm sinh không thể lớn hơn hoặc bằng ngày hiện tại!";
            }
          }
          // Gender validation
          if (!values.gender) {
            errors.gender = "Không được để trống!";
          }
          // AccountType validation
          if (!values.accountType) {
            errors.accountType = "Không được để trống!";
          }
          // PhoneNumber validation
          if (!values.phoneNumber) {
            errors.phoneNumber = "Không được để trống!";
          } else if (values.phoneNumber.length < 10) {
            errors.phoneNumber = "Số điện thoại không hợp lệ";
          } else if (
            !/((09|03|07|08|05)+([0-9]{8})\b$)/g.test(values.phoneNumber)
          ) {
            errors.phoneNumber = "Số điện thoại không hợp lệ";
          }
          // FirstName validation
          if (!values.firstName) {
            errors.firstName = "Không được để trống!";
          }
          // else if (!/^[a-zA-Z ]+$/.test(values.firstName)) {
          //   errors.firstName = "Họ không hợp lệ! Vui lòng nhập không dấu!";
          // }
          // LastName validation
          if (!values.lastName) {
            errors.lastName = "Không được để trống!";
          }
          // else if (!/^[a-zA-Z ]+$/.test(values.lastName)) {
          //   errors.lastName = "Tên không hợp lệ! Vui lòng nhập không dấu!";
          // }
          // UserName validation
          if (!values.username) {
            errors.username = "Không được để trống!";
          } else if (/\s/.test(values.username)) {
            errors.username = "Tên đăng nhập không được chứa khoảng trắng.";
          }
          // else if (!/^[a-z0-9_-]{3,16}$/.test(values.username)) {
          //   errors.username =
          //     "Tên đăng nhập phải bao gồm chuỗi và số từ 3 đến 16 ký tự chỉ được thêm kí tự '-' hoặc '_', không được để dấu, chữ cái viết hoa. Ví dụ: abc_123";
          // }

          return errors;
        }}
        onSubmit={(values, { setSubmitting }) => {
          handleSubmit(values, setSubmitting, setLoading);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
          /* and other goodies */
        }) => (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4">
              {/* FirstName & LastName */}
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="firstName">Họ</Label>
                  <Input
                    id="firstName"
                    placeholder="Nguyễn"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.firstName}
                    autoComplete="off"
                  />
                  <p className={cn("text-sm font-medium text-destructive")}>
                    {errors.firstName && touched.firstName && errors.firstName}
                  </p>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="lastName">Tên</Label>
                  <Input
                    id="lastName"
                    placeholder="Văn A"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.lastName}
                    autoComplete="off"
                  />
                  <p className={cn("text-sm font-medium text-destructive")}>
                    {errors.lastName && touched.lastName && errors.lastName}
                  </p>
                </div>
              </div>
              {/* PhoneNumber */}
              <div className="grid gap-2">
                <Label htmlFor="phoneNumber">Số điện thoại</Label>
                <Input
                  id="phoneNumber"
                  type="text"
                  placeholder="Nhập số điện thoại của bạn"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.phoneNumber}
                  autoComplete="off"
                />
                <p className={cn("text-sm font-medium text-destructive")}>
                  {errors.phoneNumber &&
                    touched.phoneNumber &&
                    errors.phoneNumber}
                </p>
              </div>
              {/* BirthDay & Gender */}
              <div className="grid tablet:grid-cols-2 gap-4">
                <div className="grid gap-2">
                  {/* BirthDay */}
                  <Label htmlFor="birthday">Ngày tháng năm sinh</Label>
                  <BirthDayPicker
                    setFieldValue={setFieldValue}
                    popOverTriggerId="birthday"
                  />
                  <p className={cn("text-sm font-medium text-destructive")}>
                    {errors.birthday && touched.birthday && errors.birthday}
                  </p>
                </div>
                <div className="grid gap-2">
                  {/* Gender */}
                  <Label htmlFor="selectGender">Giới tính</Label>
                  <GenderSelect
                    setFieldValue={setFieldValue}
                    selectTriggerId="selectGender"
                  />
                  <p className={cn("text-sm font-medium text-destructive")}>
                    {errors.gender && touched.gender && errors.gender}
                  </p>
                </div>
              </div>
              {/* Account Type */}
              <div className="grid gap-2">
                <Label htmlFor="selectAccountType">Loại tài khoản</Label>
                <AccountTypeSelect
                  setFieldValue={setFieldValue}
                  selectTriggerId="selectAccountType"
                />
                <p className={cn("text-sm font-medium text-destructive")}>
                  {errors.accountType &&
                    touched.accountType &&
                    errors.accountType}
                </p>
              </div>
              {/* UserName */}
              <div className="grid gap-2">
                <Label htmlFor="username">Tên đăng nhập</Label>
                <Input
                  id="username"
                  type="text"
                  placeholder="Nhập tên đăng nhập của bạn"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.username}
                  autoComplete="off"
                />
                <p className={cn("text-sm font-medium text-destructive")}>
                  {errors.username && touched.username && errors.username}
                </p>
              </div>
              {/* Email */}
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  name="email"
                  placeholder="Nhập email của bạn"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.email}
                  autoComplete="off"
                />
                <p className={cn("text-sm font-medium text-destructive")}>
                  {errors.email && touched.email && errors.email}
                </p>
              </div>
              {/* Password */}
              <div className="grid gap-2">
                <Label htmlFor="password">Mật khẩu</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    placeholder="Nhập mật khẩu của bạn"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.password}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    className="absolute bottom-1 right-1 h-7 w-7"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? (
                      <Eye className="h-4 w-4" />
                    ) : (
                      <EyeOff className="h-4 w-4" />
                    )}
                    <span className="sr-only">Toggle password visibility</span>
                  </Button>
                </div>
                <p className={cn("text-sm font-medium text-destructive")}>
                  {errors.password && touched.password && errors.password}
                </p>
              </div>

              <Button
                variant="green_theme_primary"
                type="submit"
                className="w-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Tạo tài khoản
                  </>
                ) : (
                  "Tạo tài khoản"
                )}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};

export default SignUpForm;
